<template>
  <!--
    Document view ("eye" action) for the accounting list. Local replacement for
    the shared dialog + pages/accounting/print.vue, which stay untouched.
  -->
  <v-dialog
    :value="value"
    :fullscreen="isMobile"
    max-width="980"
    scrollable
    content-class="adv-dialog"
    @input="$emit('input', $event)"
  >
    <div class="adv" :class="{ 'theme--dark': $vuetify.theme.isDark }" :style="rootStyle" dir="rtl">
      <!-- Header -->
      <header class="adv-head">
        <span class="adv-head__icon">
          <v-icon size="20">mdi-file-document-outline</v-icon>
        </span>
        <div class="adv-head__titles">
          <div class="adv-head__title">
            {{ $t("accountingDocument") }}
            <span class="adv-num">{{ doc ? faNum(doc.number) : "" }}</span>
          </div>
          <div class="adv-head__sub">
            <template v-if="doc">
              <span
                class="adv-pill"
                :class="docType === 'always' ? 'adv-pill--green' : 'adv-pill--amber'"
              >
                {{ docType === "always" ? $t("always") : $t("temporary") }}
              </span>
              <span>{{ $t(doc.type) }}</span>
              <span v-if="item && item.lock" class="adv-pill adv-pill--red">
                <v-icon size="12">mdi-lock</v-icon>{{ $t("accDocListLocked") }}
              </span>
              <span
                v-if="status"
                class="adv-pill adv-pill--dot"
                :style="{ '--c': status.color || '#607D8B', '--cs': (status.color || '#607D8B') + '1f' }"
              >
                {{ status.name }}
              </span>
            </template>
          </div>
        </div>
        <div class="adv-head__actions">
          <button
            v-if="item"
            type="button"
            class="adv-btn"
            :disabled="!!item.lock"
            @click="$emit('edit', item)"
          >
            <v-icon size="16">mdi-pencil-outline</v-icon>
            <span class="adv-btn__text">{{ $t("edit") }}</span>
          </button>
          <button type="button" class="adv-btn" :disabled="!doc" @click="studio = true">
            <v-icon size="16">mdi-printer-outline</v-icon>
            <span class="adv-btn__text">{{ $t("print") }}</span>
          </button>
          <button
            v-if="item"
            type="button"
            class="adv-btn adv-btn--icon"
            :title="$t('copy-link')"
            v-clipboard:copy="link"
            @click="$emit('copied')"
          >
            <v-icon size="16">mdi-link-variant</v-icon>
          </button>
          <button
            type="button"
            class="adv-btn adv-btn--icon"
            :title="$t('close')"
            @click="$emit('input', false)"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
      </header>

      <div class="adv-body">
        <!-- Loading -->
        <div v-if="loading" class="adv-skeleton">
          <div class="adv-meta">
            <div v-for="n in 6" :key="n" class="adv-meta__item">
              <span class="adv-sk" style="width: 50px" />
              <span class="adv-sk" style="width: 90px; height: 12px" />
            </div>
          </div>
          <div v-for="n in 6" :key="'r' + n" class="adv-sk-row">
            <span class="adv-sk" style="width: 20px" />
            <span class="adv-sk" style="flex: 1" />
            <span class="adv-sk" style="width: 120px" />
            <span class="adv-sk" style="width: 90px" />
            <span class="adv-sk" style="width: 90px" />
          </div>
        </div>

        <!-- Printable sheet -->
        <div v-else-if="doc" ref="sheet" class="adv-sheet">
          <div class="adv-print-head">
            <img v-if="doc.self && doc.self.logo" :src="doc.self.logo.link" alt="" />
            <div>
              <div class="adv-print-head__title">{{ $t("accountingDocument") }}</div>
              <div>{{ doc.self ? doc.self.abrName : "" }}</div>
            </div>
          </div>

          <!-- Meta -->
          <section class="adv-meta">
            <div v-for="m in meta" :key="m.label" class="adv-meta__item">
              <span class="adv-meta__label">
                <v-icon size="14">{{ m.icon }}</v-icon>{{ m.label }}
              </span>
              <span class="adv-meta__value adv-num">{{ m.value }}</span>
            </div>
          </section>

          <div v-if="doc.content" class="adv-desc">
            <span class="adv-desc__label">{{ $t("content") }}</span>
            <p>{{ faNum(doc.content) }}</p>
          </div>

          <!-- Articles -->
          <section class="adv-card">
            <div class="adv-card__head">
              <span>{{ $t("accDocViewArticles") }}</span>
              <span class="adv-muted adv-num">{{ faNum(rows.length) }}</span>
            </div>
            <div class="adv-table-scroll">
              <table class="adv-table">
                <thead>
                  <tr>
                    <th class="adv-c-row">{{ $t("row") }}</th>
                    <th>{{ $t("given") }}</th>
                    <th>{{ $t("detail") }}</th>
                    <th class="adv-c-desc">{{ $t("content") }}</th>
                    <th class="adv-c-amt">{{ $t("debtor") }}</th>
                    <th class="adv-c-amt">{{ $t("creditor") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in rows" :key="i">
                    <td class="adv-c-row adv-muted adv-num">{{ faNum(i + 1) }}</td>
                    <td>
                      <div class="adv-acc">
                        <span class="adv-acc__name">{{ r.given ? r.given.name : "—" }}</span>
                        <span v-if="r.given && r.given.code" class="adv-acc__code adv-num">
                          {{ faNum(r.given.code) }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div class="adv-acc">
                        <span class="adv-acc__name">{{ r.fin4 ? r.fin4.name : "—" }}</span>
                        <span v-if="r.fin4 && r.fin4.code" class="adv-acc__code adv-num">
                          {{ faNum(r.fin4.code) }}
                        </span>
                      </div>
                    </td>
                    <td class="adv-c-desc">{{ faNum(r.content) || "—" }}</td>
                    <td class="adv-c-amt">
                      <span :class="{ 'adv-zero': !+r.debtor }" class="adv-num">
                        {{ faNum($numberFormat(parseFloat(r.debtor) || 0)) }}
                      </span>
                    </td>
                    <td class="adv-c-amt">
                      <span :class="{ 'adv-zero': !+r.creditor }" class="adv-num">
                        {{ faNum($numberFormat(parseFloat(r.creditor) || 0)) }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!rows.length">
                    <td colspan="6" class="adv-empty">{{ $t("no-data") }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="4">
                      <span class="adv-total-label">{{ $t("sum") }}</span>
                      <span
                        class="adv-pill"
                        :class="diff ? 'adv-pill--amber' : 'adv-pill--green'"
                      >
                        <v-icon size="12">
                          {{ diff ? "mdi-scale-unbalanced" : "mdi-check" }}
                        </v-icon>
                        {{
                          diff
                            ? $t("difference") + ": " + faNum($numberFormat(diff))
                            : $t("accDocListBalanced")
                        }}
                      </span>
                    </td>
                    <td class="adv-c-amt adv-num">{{ faNum($numberFormat(totals.debtor)) }}</td>
                    <td class="adv-c-amt adv-num">{{ faNum($numberFormat(totals.creditor)) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <!-- Attachments -->
          <section v-if="attachments.length" class="adv-card">
            <div class="adv-card__head">
              <span>{{ $t("attachments") }}</span>
              <span class="adv-muted adv-num">{{ faNum(attachments.length) }}</span>
            </div>
            <div class="adv-files">
              <a
                v-for="f in attachments"
                :key="f.key"
                class="adv-file"
                :href="f.link"
                target="_blank"
                rel="noopener"
              >
                <span class="adv-file__thumb">
                  <img v-if="f.isImage" :src="f.link" alt="" />
                  <v-icon v-else size="22">mdi-file-outline</v-icon>
                </span>
                <span class="adv-file__info">
                  <span class="adv-file__name" :title="f.name">{{ f.name }}</span>
                  <span class="adv-muted">{{ f.ext }} · {{ f.size }}</span>
                </span>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
    <!-- Print through the shared print module (own templates: "accounting-doc") -->
    <print-studio
      v-if="doc"
      v-model="studio"
      report="accounting-doc"
      :title="docTitle"
      :subtitle="docSubtitle"
      :columns="printColumns"
      :source="printSource"
      @error="$emit('error', $event)"
    />
  </v-dialog>
</template>

<script>
import PrintStudio from "@/modules/print/PrintStudio.vue";

export default {
  name: "AccountingDocView",
  components: { PrintStudio },
  props: {
    value: { type: Boolean, default: false },
    // List row (id, lock, doc_type …); the full document is fetched.
    item: { type: Object, default: null },
  },
  data: () => ({
    loading: false,
    doc: null,
    studio: false,
  }),
  computed: {
    docTitle() {
      return this.$t("accountingDocument") + " " + this.faNum(this.doc ? this.doc.number : "");
    },
    docSubtitle() {
      var d = this.doc || {};
      return [
        d.date ? this.$t("date") + ": " + d.date : "",
        d.turning_number ? this.$t("turningNumber") + ": " + d.turning_number : "",
        d.content || "",
      ].filter(Boolean).join(" · ");
    },
    printColumns() {
      var named = function (o) {
        return o ? (o.code ? o.code + " - " : "") + (o.name || "") : "";
      };
      return [
        { key: "given", label: this.$t("given"), width: 2, value: function (r) { return named(r.given); } },
        { key: "fin4", label: this.$t("detail"), width: 2, value: function (r) { return named(r.fin4); } },
        { key: "content", label: this.$t("content"), width: 3 },
        { key: "count", label: this.$t("count"), format: "number", width: 0.7, defaultVisible: false },
        { key: "debtor", label: this.$t("debtor"), format: "money", sum: true, width: 1.3, value: function (r) { return parseFloat(r.debtor) || 0; } },
        { key: "creditor", label: this.$t("creditor"), format: "money", sum: true, width: 1.3, value: function (r) { return parseFloat(r.creditor) || 0; } },
      ];
    },
    printSource() {
      var app = this;
      return {
        currentRows: function () { return app.rows; },
        total: function () { return app.rows.length; },
        fetchPage: function () {
          return Promise.resolve({ rows: app.rows, lastPage: 1, total: app.rows.length });
        },
      };
    },
    isMobile() {
      return this.$vuetify.breakpoint.width < 800;
    },
    rootStyle() {
      var theme = this.$vuetify.theme;
      var t = theme.themes[theme.isDark ? "dark" : "light"] || {};
      var primary = typeof t.primary === "string" ? t.primary : "#1976d2";
      return { "--adv-primary": primary, "--adv-primary-soft": primary + "1a" };
    },
    docType() {
      return (this.doc && this.doc.doc_type) || (this.item && this.item.doc_type);
    },
    status() {
      var s = (this.doc && this.doc.last_status) || (this.item && this.item.last_status);
      return s && s.status ? s.status : null;
    },
    link() {
      return this.item ? this.$baseURL + "/accounting/edit/" + this.item.id : "";
    },
    rows() {
      return (this.doc && this.doc.article) || [];
    },
    totals() {
      return this.rows.reduce(
        function (acc, r) {
          acc.debtor += parseFloat(r.debtor) || 0;
          acc.creditor += parseFloat(r.creditor) || 0;
          return acc;
        },
        { debtor: 0, creditor: 0 }
      );
    },
    diff() {
      return Math.abs(this.totals.debtor - this.totals.creditor);
    },
    meta() {
      var d = this.doc || {};
      var user = d.user ? d.user.name || [d.user.firstname, d.user.lastname].filter(Boolean).join(" ") : "";
      return [
        { icon: "mdi-pound", label: this.$t("document-number"), value: this.faNum(d.number) || "—" },
        { icon: "mdi-calendar-blank-outline", label: this.$t("date"), value: this.faNum(d.date) || "—" },
        { icon: "mdi-sync", label: this.$t("turningNumber"), value: this.faNum(d.turning_number) || "—" },
        { icon: "mdi-calendar-today", label: this.$t("dailyNumber"), value: this.faNum(d.daily_number) || "—" },
        { icon: "mdi-account-outline", label: this.$t("registerar"), value: user || "—" },
        { icon: "mdi-clock-outline", label: this.$t("time"), value: this.faNum(d.time) || "—" },
      ];
    },
    attachments() {
      var list = (this.doc && this.doc.attachments) || [];
      var img = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
      return list.map(function (f, i) {
        var name = f.name || f.title || "-";
        var ext = String(f.extension || (name.indexOf(".") > -1 ? name.split(".").pop() : "")).replace(".", "").toLowerCase();
        var bytes = Number(f.size);
        var size = isNaN(bytes) ? "-" : bytes >= 1048576 ? (bytes / 1048576).toFixed(1) + " MB" : bytes >= 1024 ? (bytes / 1024).toFixed(0) + " KB" : bytes + " B";
        return {
          key: f.id != null ? f.id : i,
          link: f.link || "",
          name: name,
          ext: ext ? ext.toUpperCase() : "-",
          size: size,
          isImage: (f.type && String(f.type).indexOf("image/") === 0) || img.indexOf(ext) > -1,
        };
      });
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    item() {
      if (this.value) this.load();
    },
  },
  methods: {
    faNum(v) {
      if (v === null || v === undefined || v === "") return v;
      var str = String(v);
      if (!(this.$i18n && String(this.$i18n.locale).indexOf("fa") === 0)) return str;
      return str.replace(/[0-9]/g, function (d) {
        return "۰۱۲۳۴۵۶۷۸۹"[d];
      });
    },
    load() {
      if (!this.item) return;
      var app = this;
      var id = this.item.id;
      app.loading = true;
      app.doc = null;
      app.$axios
        .get(app.$endpoint + "accounting/" + id + "/edit", { params: { id: id, print: "active" } })
        .then(function (res) {
          if (app.item && app.item.id === id) app.doc = res.data;
        })
        .catch(function (err) {
          app.$emit("error", err);
        })
        .finally(function () {
          app.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.adv {
  --adv-bg: #f4f6fa;
  --adv-surface: #fff;
  --adv-surface-2: #f8fafc;
  --adv-border: #e5e9f0;
  --adv-text: #0f172a;
  --adv-text-2: #334155;
  --adv-muted: #64748b;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  background: var(--adv-bg);
  color: var(--adv-text);
  border-radius: 6px;
  overflow: hidden;
  font-size: 12.5px;
}
.adv.theme--dark {
  --adv-bg: #121212;
  --adv-surface: #1e1e1e;
  --adv-surface-2: #242424;
  --adv-border: #2e2e2e;
  --adv-text: #e2e8f0;
  --adv-text-2: #cbd5e1;
  --adv-muted: #94a3b8;
}
.adv-num {
  font-variant-numeric: tabular-nums;
  unicode-bidi: isolate;
}
.adv-muted {
  color: var(--adv-muted);
}
.adv .v-icon {
  color: inherit;
}

/* Header */
.adv-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: var(--adv-surface);
  border-bottom: 1px solid var(--adv-border);
}
.adv-head__icon {
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border-radius: 6px;
  background: var(--adv-primary-soft);
  color: var(--adv-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.adv-head__titles {
  min-width: 0;
}
.adv-head__title {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.adv-head__title .adv-num {
  color: var(--adv-primary);
  margin-inline-start: 4px;
}
.adv-head__sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 3px;
  font-size: 11.5px;
  color: var(--adv-muted);
}
.adv-head__actions {
  margin-inline-start: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}
.adv-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d5dbe5;
  border-radius: 6px;
  background: var(--adv-surface);
  color: var(--adv-text-2);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.adv-btn:hover:not(:disabled) {
  border-color: var(--adv-primary);
  color: var(--adv-primary);
}
.adv-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.adv-btn--icon {
  width: 32px;
  padding: 0;
  justify-content: center;
}

/* Pills */
.adv-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--c, var(--adv-muted));
  background: var(--cs, rgba(100, 116, 139, 0.1));
}
.adv-pill--dot::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.adv-pill--green { --c: #2e7d32; --cs: rgba(46, 125, 50, 0.1); }
.adv-pill--amber { --c: #b26a00; --cs: rgba(255, 160, 0, 0.14); }
.adv-pill--red { --c: #c62828; --cs: rgba(198, 40, 40, 0.09); }

/* Body */
.adv-body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 12px 14px 14px;
}
.adv-sheet {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.adv-print-head {
  display: none;
}

.adv-meta {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 1px;
  background: var(--adv-border);
  border: 1px solid var(--adv-border);
  border-radius: 6px;
  overflow: hidden;
}
.adv-meta__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 9px 12px;
  background: var(--adv-surface);
  min-width: 0;
}
.adv-meta__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--adv-muted);
}
.adv-meta__value {
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adv-desc {
  padding: 9px 12px;
  background: var(--adv-surface);
  border: 1px solid var(--adv-border);
  border-radius: 6px;
}
.adv-desc__label {
  font-size: 11px;
  color: var(--adv-muted);
}
.adv-desc p {
  margin: 3px 0 0;
  line-height: 1.7;
}

.adv-card {
  background: var(--adv-surface);
  border: 1px solid var(--adv-border);
  border-radius: 6px;
  overflow: hidden;
}
.adv-card__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-weight: 700;
  border-bottom: 1px solid var(--adv-border);
}
.adv-table-scroll {
  overflow-x: auto;
}
.adv-table {
  width: 100%;
  border-collapse: collapse;
}
.adv-table th {
  height: 32px;
  padding: 0 10px;
  background: var(--adv-surface-2);
  color: var(--adv-muted);
  font-size: 11px;
  font-weight: 600;
  text-align: start;
  white-space: nowrap;
  border-bottom: 1px solid var(--adv-border);
}
.adv-table td {
  height: 38px;
  padding: 4px 10px;
  border-bottom: 1px solid var(--adv-border);
  vertical-align: middle;
}
.adv-table th + th,
.adv-table td + td {
  border-inline-start: 1px solid rgba(148, 163, 184, 0.16);
}
.adv-table tbody tr:hover td {
  background: #f5f8fd;
}
.adv.theme--dark .adv-table tbody tr:hover td {
  background: #262a30;
}
.adv-c-row {
  width: 40px;
  text-align: center !important;
}
.adv-c-amt {
  width: 130px;
  direction: ltr;
  text-align: right !important;
  white-space: nowrap;
  font-weight: 600;
}
.adv-c-desc {
  color: var(--adv-text-2);
  min-width: 180px;
}
.adv-zero {
  color: var(--adv-muted);
  font-weight: 400;
}
.adv-acc {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
}
.adv-acc__name {
  font-weight: 600;
}
.adv-acc__code {
  font-size: 11px;
  color: var(--adv-muted);
}
.adv-empty {
  text-align: center;
  color: var(--adv-muted);
  padding: 20px !important;
}
.adv-table tfoot td {
  height: 40px;
  background: var(--adv-surface-2);
  font-weight: 700;
  border-bottom: 0;
}
.adv-total-label {
  margin-inline-end: 10px;
}

/* Attachments */
.adv-files {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  padding: 10px;
}
.adv-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border: 1px solid var(--adv-border);
  border-radius: 6px;
  color: inherit;
  text-decoration: none;
}
.adv-file:hover {
  border-color: var(--adv-primary);
}
.adv-file__thumb {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: 6px;
  background: var(--adv-surface-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: var(--adv-muted);
}
.adv-file__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.adv-file__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 11px;
}
.adv-file__name {
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


/* Skeleton */
.adv-skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.adv-sk-row {
  display: flex;
  gap: 16px;
  padding: 12px;
  background: var(--adv-surface);
  border-radius: 6px;
}
.adv-sk {
  display: block;
  height: 9px;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--adv-border), var(--adv-surface-2), var(--adv-border));
  background-size: 200% 100%;
  animation: adv-shimmer 1.3s linear infinite;
}
@keyframes adv-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Print window */
.adv--print {
  max-height: none;
  background: #fff;
  font-size: 11px;
}
.adv--print .adv-print-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--adv-primary);
}
.adv--print .adv-print-head img {
  width: 48px;
}
.adv--print .adv-print-head__title {
  font-size: 15px;
  font-weight: 700;
}
.adv--print .adv-card,
.adv--print .adv-meta,
.adv--print .adv-desc {
  break-inside: avoid;
}

@media (max-width: 799px) {
  .adv {
    max-height: 100vh;
    height: 100%;
    border-radius: 0;
  }
  .adv-meta {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .adv-btn__text {
    display: none;
  }
  .adv-btn {
    padding: 0 9px;
  }
}
</style>
